import * as React from 'react'
import { faClose, FaIcon } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import EditCompanyGroupDetailsModal from './EditCompanyGroupDetailsModal'
import ModalLayout from '../../../drivers/modals/shared/ModalLayout'
import EditCompanyGroupsCustomList from './components/EditCompanyGroupsCustomList'
import EditCompanyGroupsDefaultList from './components/EditCompanyGroupsDefaultList'
import useCompanyGroupService from '../../services/CompanyGroupService'
import { CompanyGroupModel } from '../../types/companyGroups'

interface EditCompanyGroupModalProps {
  close: () => void
  companyId: string
}

const EditCompanyGroupModal: React.FC<EditCompanyGroupModalProps> = ({
  close,
  companyId,
}) => {
  const { t } = useLocalize()
  const { getGroups } = useCompanyGroupService()
  const [groups, setGroups] = React.useState<CompanyGroupModel[]>([])
  const [isEditGroupModalOpen, setIsEditGroupModalOpen] = React.useState(false)
  const [selectedGroup, setSelectedGroup] = React.useState<CompanyGroupModel | null>(null)

  const isDefaultGroup = selectedGroup && selectedGroup.id < 0

  const fetchGroups = React.useCallback(async () => {
    const fetchedGroups = await getGroups({ companyId })
    setGroups(fetchedGroups)
  }, [companyId])

  React.useEffect(() => {
    document.body.style.overflow = 'hidden'
    fetchGroups()
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [])

  React.useEffect(() => {
    if (selectedGroup) {
      setIsEditGroupModalOpen(true)
    }
  }, [selectedGroup])

  React.useEffect(() => {
    // Update selectedGroup when groups change
    if (selectedGroup && groups) {
      const updatedGroup = groups.find((g) => g.id === selectedGroup.id)
      if (
        updatedGroup &&
        JSON.stringify(updatedGroup) !== JSON.stringify(selectedGroup)
      ) {
        setSelectedGroup(updatedGroup)
      }
    }
  }, [groups])

  const handleGroupUpdated = () => {
    fetchGroups()
    setIsEditGroupModalOpen(false)
    setSelectedGroup(null)
  }

  return (
    <>
      <ModalLayout>
        <div className="tw-flex tw-max-h-[500px] tw-w-full tw-max-w-[500px] tw-flex-col tw-justify-between tw-rounded-lg tw-bg-white">
          <div className="tw-flex tw-items-center tw-justify-between tw-p-4">
            <div className="tw-flex tw-flex-col tw-gap-2">
              <p className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content-base">
                {t('addCompany.editGroups')}
              </p>
              <p className="tw-text-sm tw-text-content-base/80">
                {t('addCompany.selectGroupToEdit')}
              </p>
            </div>
            <FaIcon
              icon={faClose}
              className="tw-mr-4 tw-h-5 tw-w-5 tw-cursor-pointer"
              onClick={() => close()}
            />
          </div>
          <div className="tw-overflow-auto tw-pt-0">
            <EditCompanyGroupsDefaultList setSelectedGroup={setSelectedGroup} />
            <EditCompanyGroupsCustomList
              groups={groups}
              setSelectedGroup={setSelectedGroup}
            />
          </div>
        </div>
      </ModalLayout>

      {isEditGroupModalOpen && selectedGroup && (
        <EditCompanyGroupDetailsModal
          companyId={companyId}
          group={selectedGroup}
          isDefaultGroup={isDefaultGroup}
          close={handleGroupUpdated}
          onGroupUpdated={handleGroupUpdated}
        />
      )}
    </>
  )
}

export default EditCompanyGroupModal
